import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Shield, Mail, FileText, Lock } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-cautela-purple/10 rounded-lg mb-6">
            <Shield className="text-cautela-purple" size={32} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-slate-600">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Introduction */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <p className="text-slate-700 leading-relaxed">
              Cautela Defence Services Ltd ("we", "our", or "us") is committed to protecting and respecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
              or use our services. We comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
            </p>
          </CardContent>
        </Card>

        {/* Section 1: Information We Collect */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-4">
              <FileText className="text-cautela-green flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Information We Collect</h2>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">1.1 Personal Information</h3>
                <p className="text-slate-700 leading-relaxed mb-2">
                  We may collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
                  <li>Submit a contact form or enquiry</li>
                  <li>Request information about our services</li>
                  <li>Subscribe to our newsletters or communications</li>
                  <li>Attend our events or webinars</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-3">
                  This information may include: name, email address, phone number, job title, organisation name, and any other information you choose to provide.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">1.2 Automatically Collected Information</h3>
                <p className="text-slate-700 leading-relaxed">
                  When you visit our website, we may automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies that are installed on your device. This information is used to improve our website and services.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: How We Use Your Information */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-4">
              <Lock className="text-cautela-purple flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. How We Use Your Information</h2>
              </div>
            </div>
            
            <p className="text-slate-700 leading-relaxed mb-3">We use the information we collect or receive to:</p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li>Respond to your enquiries and provide customer support</li>
              <li>Deliver the services you have requested</li>
              <li>Send you information about our services, events, and updates</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations and protect our legal rights</li>
              <li>Detect, prevent, and address technical issues or fraudulent activity</li>
            </ul>
          </CardContent>
        </Card>

        {/* Section 3: Legal Basis for Processing */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Legal Basis for Processing</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              Under UK GDPR, we process your personal data on the following legal bases:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Consent:</strong> Where you have given us clear consent to process your personal data for a specific purpose</li>
              <li><strong>Contract:</strong> Where processing is necessary for the performance of a contract with you</li>
              <li><strong>Legal Obligation:</strong> Where we need to comply with legal or regulatory requirements</li>
              <li><strong>Legitimate Interests:</strong> Where processing is necessary for our legitimate business interests, provided this does not override your rights and freedoms</li>
            </ul>
          </CardContent>
        </Card>

        {/* Section 4: Data Sharing */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Sharing Your Information</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We do not sell, rent, or trade your personal information. We may share your information with:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf (e.g., hosting, analytics)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights, property, or safety</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale, or transfer of company assets</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-3">
              All third parties are required to maintain appropriate security measures and process your data only as instructed by us.
            </p>
          </CardContent>
        </Card>

        {/* Section 5: Data Retention */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Data Retention</h2>
            <p className="text-slate-700 leading-relaxed">
              We will retain your personal information only for as long as necessary to fulfil the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements. When we no longer need your personal data, we will securely delete or anonymise it.
            </p>
          </CardContent>
        </Card>

        {/* Section 6: Your Rights */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Your Rights Under UK GDPR</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              Under UK data protection law, you have the following rights:
            </p>
            <ul className="list-disc list-inside text-slate-700 space-y-2 ml-4">
              <li><strong>Right of Access:</strong> Request copies of your personal data</li>
              <li><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data in certain circumstances</li>
              <li><strong>Right to Restrict Processing:</strong> Request that we limit the processing of your data</li>
              <li><strong>Right to Data Portability:</strong> Request transfer of your data to another organisation</li>
              <li><strong>Right to Object:</strong> Object to our processing of your personal data</li>
              <li><strong>Rights Related to Automated Decision Making:</strong> Not to be subject to automated decision-making including profiling</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              To exercise any of these rights, please contact us using the details provided below. We will respond to your request within one month.
            </p>
          </CardContent>
        </Card>

        {/* Section 7: Security */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data Security</h2>
            <p className="text-slate-700 leading-relaxed">
              We implement appropriate technical and organisational measures to protect your personal data against unauthorised or unlawful processing, accidental loss, destruction, or damage. However, please note that no method of transmission over the internet or electronic storage is 100% secure.
            </p>
          </CardContent>
        </Card>

        {/* Section 8: International Transfers */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">8. International Data Transfers</h2>
            <p className="text-slate-700 leading-relaxed">
              We primarily process data within the United Kingdom. If we transfer your data outside the UK, we will ensure that appropriate safeguards are in place to protect your information in accordance with UK GDPR requirements.
            </p>
          </CardContent>
        </Card>

        {/* Section 9: Cookies */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Cookies and Tracking Technologies</h2>
            <p className="text-slate-700 leading-relaxed">
              Our website may use cookies and similar tracking technologies to enhance your browsing experience. You can control cookies through your browser settings. For more information, please see our Cookie Policy.
            </p>
          </CardContent>
        </Card>

        {/* Section 10: Children's Privacy */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-700 leading-relaxed">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal data, please contact us.
            </p>
          </CardContent>
        </Card>

        {/* Section 11: Changes to Privacy Policy */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mb-8 border-none shadow-lg bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <CardContent className="p-8">
            <div className="flex items-start gap-4 mb-4">
              <Mail className="text-cautela-green flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="text-2xl font-bold mb-4">12. Contact Us</h2>
              </div>
            </div>
            
            <p className="text-slate-200 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or wish to exercise your rights, please complete the contact form on our homepage.
            </p>
            <div className="bg-white/10 rounded-lg p-4 space-y-2">
              <p className="text-slate-200"><strong>Cautela Defence Services Ltd</strong></p>
              <p className="text-slate-200">Address: United Kingdom</p>
            </div>
            <p className="text-slate-300 text-sm mt-4">
              You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) if you believe your data protection rights have been violated. Visit <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-cautela-green hover:underline">ico.org.uk</a> for more information.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;